import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useLocalStorage } from '@vueuse/core'

import { authApi, type AuthUser } from '@/lib/auth/api'

/**
 * Auth session for the web app. Email/password against the backend; the token
 * pair + user persist to localStorage so a refresh keeps you signed in. The app
 * stays guest-first — being signed out is a valid state, you just work locally.
 */
export const useAuthStore = defineStore('auth', () => {
  const accessToken = useLocalStorage<string | null>('nurage.auth.accessToken', null)
  const refreshToken = useLocalStorage<string | null>('nurage.auth.refreshToken', null)
  const user = useLocalStorage<AuthUser | null>('nurage.auth.user', null)

  const isAuthenticated = computed(() => !!accessToken.value)

  function setSession(result: {
    user: AuthUser
    tokens: { accessToken: string; refreshToken: string }
  }) {
    accessToken.value = result.tokens.accessToken
    refreshToken.value = result.tokens.refreshToken
    user.value = result.user
  }

  function clear() {
    accessToken.value = null
    refreshToken.value = null
    user.value = null
  }

  async function login(email: string, password: string) {
    setSession(await authApi.login(email, password))
  }

  async function register(email: string, password: string, name: string) {
    setSession(await authApi.register(email, password, name))
  }

  async function logout() {
    const rt = refreshToken.value
    clear()
    if (rt) {
      try {
        await authApi.logout(rt)
      } catch {
        // Best-effort — local state is already cleared.
      }
    }
  }

  /**
   * Swap the (expired) access token for a fresh pair using the stored refresh
   * token. Returns true on success. On a hard failure (refresh token expired /
   * revoked) the session is cleared — that's a real logout. A single in-flight
   * refresh is shared so concurrent 401s don't stampede the endpoint.
   */
  let refreshInFlight: Promise<boolean> | null = null
  function refresh(): Promise<boolean> {
    if (refreshInFlight) return refreshInFlight
    refreshInFlight = (async () => {
      const rt = refreshToken.value
      if (!rt) return false
      try {
        const tokens = await authApi.refresh(rt)
        accessToken.value = tokens.accessToken
        refreshToken.value = tokens.refreshToken
        return true
      } catch {
        clear()
        return false
      } finally {
        refreshInFlight = null
      }
    })()
    return refreshInFlight
  }

  /** Restore + validate a persisted session on app start. */
  async function loadSession() {
    if (!accessToken.value) return
    try {
      user.value = await authApi.me(accessToken.value)
    } catch {
      // Access token stale — try one refresh before giving up (a 7-day refresh
      // token should keep the session alive across the 15-min access expiry).
      if (await refresh()) {
        try {
          user.value = await authApi.me(accessToken.value!)
        } catch {
          clear()
        }
      }
    }
  }

  return {
    accessToken,
    refreshToken,
    user,
    isAuthenticated,
    login,
    register,
    logout,
    loadSession,
    refresh,
  }
})
